import React, {
  useState,
  useCallback,
  Fragment,
  useMemo,
  useRef,
} from "react";
import _ from "lodash";
import moment from "moment";
import TableComponent from "components/Table";
import PageWrapper from "components/wrappers/PageWrapper";
import { pickChannel, pickStatus } from ".";
import { useTranslation } from "react-i18next";
import NotificationDetails from "./NotificationDetails";
import AddNotification from "./AddNotification";
import CampaignService from "api/services/CampaignService";
import ToastAlert from "components/ToastAlert";
import { t } from "i18next";
import OnDeleteConfirm from "components/modals/OnDeleteConfirm";
import OnCancelConfirm from "components/modals/OnCancelConfirm";
import PropTypes from 'prop-types';

NotifTitleCell_.propTypes = {
    data: PropTypes.array,
    row: PropTypes.shape({ id: PropTypes.string }),
};
NotifStatusCell_.propTypes = {
    value: PropTypes.array,
};
NotifChannelsCell_.propTypes = {
    value: PropTypes.arrayOf(PropTypes.shape({ _id: PropTypes.string, enabled: PropTypes.bool })),
};
NotifScheduleCell_.propTypes = {
    value: PropTypes.any,
};
NotifActionsCampaignCell.propTypes = {
    data: PropTypes.array,
    row: PropTypes.shape({ id: PropTypes.string }),
    column: PropTypes.shape({ onCancel: PropTypes.func, onDelete: PropTypes.func }),
};

function NotifTitleCell_({ data, row }) {
    return (
        <div className="max-w-lg">
            <div className="font-bold">{data[row.id]?.notification?.title}</div>
            <div className="text-xs text-ellipsis truncate">{data[row.id]?.notification?.text}</div>
        </div>
    );
}
function NotifStatusCell_({ value }) {
    return (
        <span className="text-xs font-medium">
            {pickStatus(value?.[0]?._id, value?.[0]?.label)}
        </span>
    );
}
function NotifChannelsCell_({ value }) {
    return (
        <div className="flex">
            {value.map((channel) => (
                <Fragment key={channel._id}>
                    <span className="text-xl">{pickChannel(channel?._id, channel?.enabled)}</span>
                </Fragment>
            ))}
        </div>
    );
}
function NotifScheduleCell_({ value }) {
    return (
        <span className="text-xs rounded px-2 py-1 text-gray-600 bg-gray-100">
            {moment(value).local().format("DD MMM YYYY - HH:mm")}
        </span>
    );
}
function NotifActionsCampaignCell({ data, row, column }) {
    const id = data[row.id]?._id;
    const notification = data[row.id];
    return (
        <Fragment>
            <OnCancelConfirm disabled={notification.currentStatus !== 1} onConfirm={() => column.onCancel(id)} key={"d4-1"} />
            <OnDeleteConfirm onConfirm={() => column.onDelete(id)} key={"d3-1"} />
            <NotificationDetails data={notification} />
        </Fragment>
    );
}

function NotificationList_() {
  const { t } = useTranslation();

  const [pageCount, setPageCount] = useState(0);
  const [data, setData] = useState([]);
  const fetchIdRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [successDelete, setSuccessDelete] = useState(null);
  const [successUpdate, setSuccessUpdate] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const fetchAPIData = async ({ limit, skip, search }) => {
    try {
      setLoading(true);
      const response = await CampaignService.getNotificationListWithPagination(limit, skip, search)
      setData(response.data);
      setPageCount(response.paging.pages);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const fetchData = useCallback(
    ({ pageSize, pageIndex }) => {
      const fetchId = ++fetchIdRef.current;
      setLoading(true);
      if (fetchId === fetchIdRef.current) {
        fetchAPIData({
          limit: pageSize,
          skip: pageSize * pageIndex,
          search: searchTerm,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchTerm, successDelete, successUpdate]
  );

  const _handleSearch = _.debounce(
    (search) => {
      setSearchTerm(search);
    },
    1500,
    {
      maxWait: 1500,
    }
  );

  function resetActionStates() {
    setSuccessDelete(null)
    setSuccessUpdate(null)
  }

  // APIs Calls
  const onAddNotification = (payload) => {
    resetActionStates()
    if (payload) {
      CampaignService.createNotification(payload).then((resp) => {
        setSuccessUpdate(true)
      }).catch((err) => {
        //Handle error ..
        setSuccessUpdate(false)
      });
    }

  }

  const onDelete = (id) => {
    resetActionStates()
    if (id) {
      CampaignService.deleteNotification(id).then((resp) => {
        setSuccessDelete(true)
        // history.go()
      }).catch((err) => {
        //Handle error ..
        setSuccessDelete(false)
      });
    }

  }

  const onCancel = (id) => {
    resetActionStates()
    if (id) {
      CampaignService.cancelNotification(id).then((resp) => {
        setSuccessDelete(true)
        // history.go()
      }).catch((err) => {
        //Handle error ..
        setSuccessDelete(false)
      });
    }

  }

  const columns = useMemo(() => [
    {
      Header: t('campaigns.notification'),
      accessor: "notification.title",
      show: true,
      Cell: NotifTitleCell_,
    },
    {
      Header: t('campaigns.status'),
      accessor: "status",
      show: true,
      Cell: NotifStatusCell_,
    },
    {
      Header: t('campaigns.channels'),
      accessor: "channels",
      show: true,
      sort: true,
      Cell: NotifChannelsCell_,
    },
    {
      Header: t('form.time'),
      accessor: "schedule",
      Cell: NotifScheduleCell_,
      show: true,
    },
    {
      Header: "",
      accessor: "actions",
      onCancel,
      onDelete,
      Cell: NotifActionsCampaignCell,
      show: true,
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);


  return (

    <PageWrapper
      title={t('campaigns.notifications')}
      actions={<AddNotification
        onDataSubmit={onAddNotification} />}
      className="border"
    >

      <div className="mt-4">
        {/* <LayoutWrapper error={error} loading={loading} > */}

        <div className="container mx-auto flex flex-col">
          <div className="mt-3">
            <input
              id="searchTerm"
              name="searchTerm"
              type="text"
              onChange={(e) => _handleSearch(e.target.value)}
              placeholder="Search by notification title"
              className="appearance-none block w-1/4 float-right px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            />
          </div>
          <div className="flex justify-center">
            <TableComponent
              pageCount={pageCount}
              fetchData={fetchData}
              columns={columns}
              loading={loading}
              data={data}
            />
          </div>
        </div>

        {/* </LayoutWrapper> */}
        <ActionsAlert successUpdate={successUpdate} successDelete={successDelete} />

      </div>

    </PageWrapper>
  );
}

export default NotificationList_;


function ActionsAlert({ successUpdate, successDelete }) {
  return (
    <>
      {successUpdate === true && <ToastAlert message={t('alert.success_operation')} type={'success'} timeOut={5000} />}
      {successUpdate === false && <ToastAlert message={t('alert.failed_operation')} type={'error'} timeOut={5000} />}
      {successDelete === true && <ToastAlert message={t('alert.success_operation')} type={'success'} timeOut={5000} />}
      {successDelete === false && <ToastAlert message={t('alert.failed_operation')} type={'error'} timeOut={5000} />}
    </>
  )
}



ActionsAlert.propTypes = {
  successUpdate: PropTypes.bool,
  successDelete: PropTypes.bool,
};
